import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';

import { environment } from '@environment';

import { MenuService } from '@core/services/menu/menu.service';
import { CartService } from '@core/services/cart/cart.service';
import { LoginService } from '@core/services/login/login.service';
import { DeviceService } from '@core/services/util/device.service';
import { OutletService } from '@core/services/outlet/outlet.service';
import { StorageService } from '@core/services/util/storage.service';
import { PlatformService } from '@core/services/util/platform.service';
import { LocalStorageService } from '@core/services/util/local-storage.service';

import { FreebieDialogComponent } from '@app/shared/components/freebie-dialog/freebie-dialog.component';
import {
    FreebieConditionsComponent,
    FreebieConditionsBottomSheet
} from '@app/modules/menu/freebie-conditions/freebie-conditions.component';

import { MenuModel } from '@app/shared/models/menu-model';

@Injectable({
    providedIn: 'root'
})
export class FreebieService {
    outlet: any;
    menuData: any;
    cart_items: any;

    popUpDetails: { [key: string]: string } = {};

    brandIdsToExclude: number[] = [];
    excludedBrandList: number[] = [];
    excludedProductList: number[] = [];

    countOfSamplingProductsAdded = [];

    samplingProductsIds = new Set<number>();
    productIdsToExclude = new Set<any>();

    state: BehaviorSubject<any[]> = new BehaviorSubject([]);

    multiItemStripText = '';

    isMobile = false;
    samplingDialogShown = false;
    previousOutletId = 0;

    constructor(
        private dialog: MatDialog,
        private bottomSheet: MatBottomSheet,
        private outletService: OutletService,
        private menuService: MenuService,
        private cartService: CartService,
        private localStorage: LocalStorageService,
        private storageService: StorageService,
        private loginService: LoginService,
        private platformService: PlatformService,
        private deviceService: DeviceService
    ) {
        if (this.platformService.isServer()) {
            return;
        }

        this.outletService.state.subscribe((data: any) => {
            if (data && Object.keys(data).length) {
                this.init();
            }
        });

        this.cartService.state.subscribe((cart: any) => {
            this.cart_items = Object.values(cart.items);
            if (this.cart_items.length) {
                this.addOrRemoveSampling();
            }
        });

        this.isMobile = this.deviceService.isMobile();
        this.loginService.userLoginSuccess.subscribe(() => this.addOrRemoveSampling());
        this.loginService.userLogoutSuccess.subscribe(() => this.addOrRemoveSampling());
    }

    private _getFreebieCart() {
        let cart = this.localStorage.get('freebie_cart');

        if (!cart) {
            cart = Object.create(null, {});
            this.localStorage.set('freebie_cart', cart);
        }

        return cart;
    }

    private _isLoggedIn() {
        return this.storageService.get('session-id') && this.storageService.get('phone_no');
    }

    async init() {
        this._getFreebieCart();
        const active_brand = environment.brand.id;
        const outletData = await this.outletService.getOutletDataWithoutStateUpdate(active_brand);

        if (outletData && outletData.outlet && this.previousOutletId !== outletData.outlet.id) {
            this.outlet = this.outletService.convertOutletData(outletData);
            this.previousOutletId = this.outlet.id;

            if (this.outlet && this.outlet.sampling_ids.length) {
                const { menuModel } = this.menuService.generateMenuModel(this.outlet);
                this.menuData = menuModel;
                const samplingProducts = this.createSamplingInfo();
                this.hydrateSamplingInfo(samplingProducts);
            } else {
                this.state.next([]);
            }
        }
    }

    createSamplingInfo() {
        const { id: outlet_id, menu_items, menu_id, sampling_details, sampling_ids } = this.outlet;
        const { items } = sampling_details;
        const samplingProducts = [];
        for (const idx of sampling_ids) {
            const productSize = this.menuData.product_sizes[idx];
            const menuItemType = menu_items.filter((data: any) => data.pid === idx)[0];

            if (!productSize || productSize.sold_out || (menuItemType && menuItemType.item_type !== 'si')) {
                continue;
            }

            const Ids = {
                product_id: productSize.product_id,
                product_size_id: productSize.id,
                customization_ids: '',
                outlet_id,
                menu_id
            };

            const samplingDetails = items.find((samplingItem: any) => samplingItem.product_size_id === idx);

            samplingProducts.push({
                samplingLabel: samplingDetails.label,
                ...samplingDetails,
                ...this.addMoreInfo(Ids)
            });
        }
        return samplingProducts;
    }

    addMoreInfo(Ids) {
        const { product_id, product_size_id, customization_ids, outlet_id, menu_id } = Ids;

        const { price, marked_price } = this.cartService._calculatePrice(this.menuData, product_size_id, customization_ids);
        const itemId = [outlet_id, menu_id, product_size_id, customization_ids].filter(Boolean).join('-');

        const product = this.menuData.products[product_id];
        const { label, description } = product;

        return {
            ...this.cartService._addMoreInfo(Ids, this.menuData),
            price,
            marked_price,
            id: itemId,
            remark: '',
            quantity: 1,
            label,
            description
        };
    }

    hydrateSamplingInfo(samplingProducts) {
        const { ex_b_ids, ex_p_ids, popup_body, popup_header, popup_footer, override_texts, multi_item_strip_text } =
            this.outlet.sampling_details;
        this.brandIdsToExclude = ex_b_ids;

        Object.values(ex_p_ids).forEach((ids: number[]) => {
            ids.forEach((id: number) => {
                this.productIdsToExclude.add(id);
            });
        });

        this.popUpDetails = {
            popup_body,
            popup_header,
            popup_footer,
            override_texts
        };

        this.multiItemStripText = multi_item_strip_text;
        samplingProducts.sort((first, second) => first.min_cart_amount - second.min_cart_amount);
        this.state.next(samplingProducts);

        this.samplingProductsIds = this.getSamplingProductSizeIds();
        this.removePreviousSampling();
    }

    nextSamplingToShow() {
        const cartValue = this.getFinalCartMarkedPrice() as number;
        const cartItemsProductSizeIds = new Set(this.cart_items?.map((data: any) => data.ids.product_size_id));

        for (const sampling of this.state.value) {
            if (
                !cartItemsProductSizeIds.has(sampling.product_size_id) &&
                sampling.min_cart_amount >= cartValue &&
                this.conditionForSampling(sampling)
            ) {
                const minCart = sampling.min_cart_amount;
                return this.state.value.filter((data: any) => {
                    return data.min_cart_amount === minCart && this.conditionForSampling(data);
                });
            }
        }
    }

    conditionForSampling(sampling) {
        const isNewCustomerEligible = this.checkNewCustomer(sampling.new_customer);
        const isLoggedInEligible = this.checkLoggedIn(sampling.sign_in);
        const isCartHasItemEligible = this.checkCartHasItem(parseInt(sampling.cart_has_item));
        return isNewCustomerEligible && isLoggedInEligible && isCartHasItemEligible;
    }

    getSamplingStripText(cartValue: number, samplings): string {
        let stripText = '';
        const diffPrice = samplings[0].min_cart_amount - cartValue;

        if (samplings.length > 1) {
            const samplingLabels = samplings.map(sampling => sampling.samplingLabel);
            const totalSamplingLabel =
                samplingLabels.length > 1
                    ? samplingLabels.slice(0, -1).join(', ') + ' and ' + samplingLabels[samplingLabels.length - 1]
                    : samplingLabels[0] || '';

            stripText = this.multiItemStripText.replace('{{xxx}}', String(diffPrice));
            stripText = stripText.replace('{{product_labels}}', totalSamplingLabel);
        } else {
            stripText = samplings[0].strip_text.replace('{{xxx}}', String(diffPrice));
        }

        return stripText;
    }

    getFinalCartMarkedPrice(): number {
        const cartItems = this.cart_items ? this.cart_items : {};

        const filteredItems = Object.values(cartItems).filter((item: any) => {
            const shouldExcludeItem =
                !this.samplingProductsIds.has(item.ids.product_size_id) &&
                !this.productIdsToExclude.has(item.ids.product_id) &&
                !(this.brandIdsToExclude.indexOf(item.brand_id) > -1);

            return shouldExcludeItem;
        });

        const finalMarkedPrice = filteredItems.reduce((acc: number, item: any) => {
            // If it's a subscription item, use the 'price' property; otherwise, use 'marked_price'
            const priceToUse = item.is_subscription_item ? item.price : item.marked_price;
            const totalPriceForItem = priceToUse * item.quantity;
            return acc + totalPriceForItem;
        }, 0) as number;

        return finalMarkedPrice;
    }

    getSamplingProductSizeIds() {
        return new Set(this.state.value.map((sampling: any) => sampling.ids.product_size_id));
    }

    checkLastOrderDays(samplingLastOrderDays: number) {
        const customerLastOrderDay = this.storageService.get('last_order_number_of_days') || 10000;
        return samplingLastOrderDays <= customerLastOrderDay;
    }

    checkNewCustomer(samplingNewCustomer: boolean) {
        if (!samplingNewCustomer) {
            return true;
        }

        let isNewCustomer = this.storageService.get('new_customer') || false;
        isNewCustomer = isNewCustomer === 'true' ? true : false;
        return isNewCustomer;
    }

    checkLoggedIn(samplingSignIn) {
        if (!samplingSignIn) {
            return true;
        }
        const isLoggedIn = this._isLoggedIn() ? true : false;
        return isLoggedIn;
    }

    checkCartHasItem(samplingCartHasProductSizeId) {
        if (!samplingCartHasProductSizeId) {
            return true;
        }
        const cartItemsProductSizeIds = new Set(this.cart_items?.map((data: any) => data.ids.product_size_id));
        return cartItemsProductSizeIds.has(samplingCartHasProductSizeId);
    }

    checkSamplingAddConditions(sampling) {
        const cartValue = this.getFinalCartMarkedPrice() as number;
        const localFreebieCart = this.localStorage.get('freebie_cart');
        const isLocalFreebieCartHasSampling = localFreebieCart && sampling.product_size_id in localFreebieCart;
        const isLastOrderDaysEligible = this.checkLastOrderDays(sampling.last_order_days);
        const isNewCustomerEligible = this.checkNewCustomer(sampling.new_customer);
        const isLoggedInEligible = this.checkLoggedIn(sampling.sign_in);
        const isCartHasItemEligible = this.checkCartHasItem(parseInt(sampling.cart_has_item));
        const isSamplingMinCartValueEligible = sampling.min_cart_amount <= cartValue;

        const commonEligibility = isSamplingMinCartValueEligible && !isLocalFreebieCartHasSampling;

        const specificEligibility =
            sampling.operator === 'AND'
                ? isLastOrderDaysEligible && isNewCustomerEligible && isLoggedInEligible && isCartHasItemEligible
                : isLastOrderDaysEligible || isNewCustomerEligible || isLoggedInEligible || isCartHasItemEligible;

        return commonEligibility && specificEligibility;
    }

    checkSamplingRemoveCondition(sampling): boolean {
        const cartValue = this.getFinalCartMarkedPrice() as number;
        const localFreebieCart = this.localStorage.get('freebie_cart');
        const isLocalFreebieCartHasSampling = localFreebieCart && sampling.product_size_id in localFreebieCart;
        const isNewCustomerEligible = this.checkNewCustomer(sampling.new_customer);
        const isLoggedInEligible = this.checkLoggedIn(sampling.sign_in);
        const isCartHasItemEligible = this.checkCartHasItem(parseInt(sampling.cart_has_item));
        const isSamplingMinCartValueEligible = sampling.min_cart_amount <= cartValue;

        if (isLocalFreebieCartHasSampling) {
            if (!isSamplingMinCartValueEligible) {
                // Cart value is lesser than sampling's min cart amount
                return true;
            }

            const conditionsMet =
                sampling.operator === 'AND'
                    ? isNewCustomerEligible && isLoggedInEligible && isCartHasItemEligible
                    : isNewCustomerEligible || isLoggedInEligible || isCartHasItemEligible;

            // Return true if the conditions are not met
            return !conditionsMet;
        }

        return false;
    }

    addOrRemoveSampling() {
        let freebieChecked = false;
        // adding sampling
        for (const sampling of this.state.value) {
            const localFreebieCart = this.localStorage.get('freebie_cart');

            if (this.checkSamplingAddConditions(sampling)) {
                const { product_id, product_size_id, customization_ids } = sampling.ids;
                localFreebieCart[product_size_id] = true;
                this.localStorage.set('freebie_cart', localFreebieCart);
                this.countOfSamplingProductsAdded.push(sampling);
                this.cartService.addItem(product_id, product_size_id, customization_ids, { menuData: this.menuData });
                freebieChecked = true;
            }
        }

        if (this.countOfSamplingProductsAdded.length) {
            this.samplingDialogShown = false;
            this.samplingDialog();
            this.countOfSamplingProductsAdded = [];
            this.cartService.freebiePopulated.next(true);
            return; // if sampling added then, there won't be any operation of removing
        }
        //removing sampling
        for (const sampling of this.state.value) {
            const localFreebieCart = this.localStorage.get('freebie_cart');
            if (this.checkSamplingRemoveCondition(sampling)) {
                // freebie is present in cart in added state
                if(localFreebieCart[sampling.ids.product_size_id] === true) {
                    delete localFreebieCart[sampling.ids.product_size_id];
                    this.localStorage.set('freebie_cart', localFreebieCart);
                    this.cartService.decrementQuantity(sampling);
                    freebieChecked = true;
                } else {
                    delete localFreebieCart[sampling.ids.product_size_id];
                    this.localStorage.set('freebie_cart', localFreebieCart);
                }
            }
        }

        if (!freebieChecked) {
            this.cartService.freebiePopulated.next(true);
        }
    }

    toggleSamplingItem(sampling: any, label: string) {
        const localSamplingCart = this.localStorage.get('freebie_cart');
        const { product_id, product_size_id, customization_ids } = sampling.ids;

        if (label === 'ADD') {
            this.cartService.addItem(product_id, product_size_id, customization_ids, { menuData: this.menuData });
            localSamplingCart[product_size_id] = true;
            this.localStorage.set('freebie_cart', localSamplingCart);
        }

        if (label === 'REMOVE') {
            this.cartService.decrementQuantity(sampling);
            localSamplingCart[product_size_id] = false;
            this.localStorage.set('freebie_cart', localSamplingCart);
        }
    }

    removePreviousSampling() {
        //remove stale sampling product
        const cartItems = Object.values(this.cartService.state.value.items);
        const localFreebieCart = this.localStorage.get('freebie_cart');

        for (const id in localFreebieCart) {
            if (!this.samplingProductsIds.has(parseInt(id))) {
                const sampling = cartItems.filter(item => item.ids.product_size_id === parseInt(id));
                delete localFreebieCart[id];
                this.localStorage.set('freebie_cart', localFreebieCart);

                if (sampling.length) {
                    this.cartService.decrementQuantity(sampling[0]);
                }
            }
        }
    }

    samplingDialog() {
        if (!this.countOfSamplingProductsAdded.length || this.samplingDialogShown) {
            return;
        }

        const dialogConfig = new MatDialogConfig();
        dialogConfig.panelClass = 'freebie-dialog';
        dialogConfig.maxWidth = 350;
        dialogConfig.minWidth = 350;
        dialogConfig.data = {
            samplings: this.countOfSamplingProductsAdded,
            popUpDetails: this.popUpDetails
        };
        this.dialog.open(FreebieDialogComponent, dialogConfig);

        this.samplingDialogShown = true;
    }

    getSamplingStrip(brand_id: number) {
        if (this.excludedBrandList.indexOf(brand_id) > -1) {
            return '';
        }

        const noOfNextSampling = this.nextSamplingToShow();
        if (!noOfNextSampling) {
            return '';
        }

        const cartValue = this.getFinalCartMarkedPrice() as number;
        return this.getSamplingStripText(cartValue, noOfNextSampling);
    }

    openFreebieTC(products, brandId) {
        const brandNameStrip = this.getExcludedBrandNames();
        const productLabels = this.excludedProductList.map(id => products[id].label);
        const existingBrand = this.outlet.memberships.find(data => data.id === brandId);
        const existingBrandName = existingBrand?.label;

        const data = {
            brandNameStrip,
            existingBrandName,
            productLabels
        };

        if (this.isMobile) {
            this.bottomSheet.open(FreebieConditionsBottomSheet, {
                data,
                panelClass: 'repeat-customization-sheet'
            });
        } else {
            const dialogConfig = new MatDialogConfig();
            dialogConfig.data = data;
            dialogConfig.autoFocus = false;
            dialogConfig.panelClass = 'freebie-details-dialog';
            this.dialog.open(FreebieConditionsComponent, dialogConfig);
        }
    }

    getExcludedBrandNames(): string {
        const excludedBrandNames = this.outlet.memberships
            .filter((brand: any) => this.brandIdsToExclude.includes(brand.id))
            .map((brand: any) => brand.label);

        const brandNameStrip =
            excludedBrandNames.length > 1
                ? excludedBrandNames.slice(0, -1).join(', ') + ' and ' + excludedBrandNames[excludedBrandNames.length - 1]
                : excludedBrandNames[0] || '';

        return brandNameStrip;
    }

    getExcludedProductList(menuModel: MenuModel): boolean {
        if (menuModel?.products && Object.values(menuModel.products).length && this.outlet && this.outlet.sampling_details) {
            const { ex_p_ids, ex_b_ids } = this.outlet.sampling_details;
            const brandExclusions = ex_p_ids[menuModel.brand_id] || [];

            this.excludedBrandList = ex_b_ids || [];
            this.excludedProductList = brandExclusions.filter(
                (id: number) => menuModel.products[id] && !menuModel.products[id].is_subscription_item
            );

            return Boolean(this.excludedBrandList.length || this.excludedProductList.length);
        }

        return false;
    }
}
